import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export default function AdminExamProductsPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<any>(null);
  const [creating, setCreating] = useState(false);

  const emptyForm = {
    titleEn: '', titleAr: '', descriptionEn: '', descriptionAr: '',
    shortDescriptionEn: '', shortDescriptionAr: '',
    computerPrice: 0, paperPrice: 0, paperEnabled: false,
    currency: 'IQD', slug: '', isPublished: 1,
  };
  const [form, setForm] = useState(emptyForm);

  const { data: products, isLoading } = useQuery({
    queryKey: ['admin-exam-products'],
    queryFn: async () => {
      const res = await fetch('/api/admin/exam-products', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (data: any) => {
      const url = data.id ? '/api/admin/exam-products/' + data.id : '/api/admin/exam-products';
      const method = data.id ? 'PATCH' : 'POST';
      const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(data) });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-exam-products'] });
      setEditing(null); setCreating(false); setForm(emptyForm);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch('/api/admin/exam-products/' + id, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['admin-exam-products'] }); },
  });

  const openEdit = (p: any) => {
    setForm({
      titleEn: p.titleEn || '', titleAr: p.titleAr || '',
      descriptionEn: p.descriptionEn || '', descriptionAr: p.descriptionAr || '',
      shortDescriptionEn: p.shortDescriptionEn || '', shortDescriptionAr: p.shortDescriptionAr || '',
      computerPrice: p.computerPrice || 0, paperPrice: p.paperPrice || 0,
      paperEnabled: p.paperPrice !== null && p.paperPrice !== undefined,
      currency: p.currency || 'IQD', slug: p.slug || '', isPublished: p.isPublished ?? 1,
    });
    setEditing(p);
    setCreating(false);
  };

  const openCreate = () => {
    setForm(emptyForm);
    setEditing(null);
    setCreating(true);
  };

  const handleSave = () => {
    const payload = { ...form, id: editing?.id };
    saveMutation.mutate(payload);
  };

  const labelStyle = 'block text-sm font-semibold text-gray-700 mb-1';
  const inputStyle = 'w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none';

  if (isLoading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Exam Products</h1>
          <p className="text-gray-500 mt-1">Create and manage certification exams</p>
        </div>
        <button onClick={openCreate} className="px-5 py-2.5 bg-yellow-700 text-white rounded-lg text-sm font-semibold hover:bg-yellow-800">
          + New Exam
        </button>
      </div>

      {/* Products list */}
      {!products?.length && !creating ? (
        <div className="bg-white border rounded-xl p-8 text-center text-gray-500">No exam products yet</div>
      ) : (
        <div className="grid gap-4">
          {products?.map((p: any) => (
            <div key={p.id} className="bg-white border rounded-xl p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-bold text-lg">{p.titleEn}</h3>
                    <span className={'px-2.5 py-0.5 rounded-full text-xs font-semibold ' + (p.isPublished ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600')}>
                      {p.isPublished ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  {p.titleAr && <p className="text-sm text-gray-500 mb-2" dir="rtl">{p.titleAr}</p>}
                  <p className="text-sm text-gray-600 mb-3">{p.shortDescriptionEn}</p>
                  <div className="flex gap-4 text-sm">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                      <span className="text-blue-600 font-semibold">💻 Computer: </span>
                      <span className="font-bold">{p.computerPrice?.toLocaleString()} {p.currency}</span>
                    </div>
                    {p.paperPrice !== null && p.paperPrice !== undefined ? (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
                        <span className="text-orange-600 font-semibold">📝 Paper: </span>
                        <span className="font-bold">{p.paperPrice?.toLocaleString()} {p.currency}</span>
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-400 text-sm">
                        📝 Paper: Disabled
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button onClick={() => openEdit(p)} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">Edit</button>
                  <button onClick={() => { if (window.confirm('Delete this exam product?')) deleteMutation.mutate(p.id); }}
                    className="px-3 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200">🗑</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(creating || editing) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto py-8">
          <div className="bg-white rounded-xl w-full max-w-2xl mx-4 shadow-xl">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold">{editing ? 'Edit Exam Product' : 'Create New Exam Product'}</h2>
            </div>
            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Slug */}
              <div>
                <label className={labelStyle}>URL Slug</label>
                <input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="itil-5-foundation-exam" className={inputStyle} />
                <p className="text-xs text-gray-400 mt-1">Leave empty to auto-generate from title</p>
              </div>

              {/* Titles */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelStyle}>Title (English) *</label>
                  <input value={form.titleEn} onChange={e => setForm({ ...form, titleEn: e.target.value })} placeholder="ITIL 5 Foundation Exam" className={inputStyle} />
                </div>
                <div>
                  <label className={labelStyle}>Title (Arabic)</label>
                  <input value={form.titleAr} onChange={e => setForm({ ...form, titleAr: e.target.value })} placeholder="امتحان ITIL 5 الأساسي" className={inputStyle} dir="rtl" />
                </div>
              </div>

              {/* Short descriptions */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelStyle}>Short Description (EN)</label>
                  <textarea value={form.shortDescriptionEn} onChange={e => setForm({ ...form, shortDescriptionEn: e.target.value })} rows={2} className={inputStyle} placeholder="Brief description for exam card..." />
                </div>
                <div>
                  <label className={labelStyle}>Short Description (AR)</label>
                  <textarea value={form.shortDescriptionAr} onChange={e => setForm({ ...form, shortDescriptionAr: e.target.value })} rows={2} className={inputStyle} dir="rtl" placeholder="وصف مختصر..." />
                </div>
              </div>

              {/* Full descriptions */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelStyle}>Full Description (EN)</label>
                  <textarea value={form.descriptionEn} onChange={e => setForm({ ...form, descriptionEn: e.target.value })} rows={3} className={inputStyle} placeholder="Full exam details..." />
                </div>
                <div>
                  <label className={labelStyle}>Full Description (AR)</label>
                  <textarea value={form.descriptionAr} onChange={e => setForm({ ...form, descriptionAr: e.target.value })} rows={3} className={inputStyle} dir="rtl" placeholder="وصف كامل..." />
                </div>
              </div>

              {/* Pricing */}
              <div className="bg-gray-50 border rounded-lg p-4 space-y-4">
                <h3 className="font-semibold text-gray-800">Pricing</h3>
                <div>
                  <label className={labelStyle}>Currency</label>
                  <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} className={inputStyle}>
                    <option value="IQD">IQD (Iraqi Dinar)</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
                <div>
                  <label className={labelStyle}>💻 Computer-Based Price *</label>
                  <input type="number" value={form.computerPrice} onChange={e => setForm({ ...form, computerPrice: Number(e.target.value) })} className={inputStyle} />
                </div>
                <div className="border-t pt-4">
                  <div className="flex items-center gap-3 mb-3">
                    <input type="checkbox" checked={form.paperEnabled} onChange={e => setForm({ ...form, paperEnabled: e.target.checked })}
                      id="paperToggle" className="w-4 h-4" style={{ accentColor: '#b8860b' }} />
                    <label htmlFor="paperToggle" className="text-sm font-semibold text-gray-700 cursor-pointer">
                      📝 Enable Paper-Based Option (Baghdad)
                    </label>
                  </div>
                  {form.paperEnabled && (
                    <div>
                      <label className={labelStyle}>Paper-Based Price</label>
                      <input type="number" value={form.paperPrice} onChange={e => setForm({ ...form, paperPrice: Number(e.target.value) })} className={inputStyle} />
                    </div>
                  )}
                </div>
              </div>

              {/* Published toggle */}
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={form.isPublished === 1} onChange={e => setForm({ ...form, isPublished: e.target.checked ? 1 : 0 })}
                  id="publishToggle" className="w-4 h-4" style={{ accentColor: '#16a34a' }} />
                <label htmlFor="publishToggle" className="text-sm font-semibold text-gray-700 cursor-pointer">Published (visible to students)</label>
              </div>
            </div>

            <div className="p-6 border-t flex gap-3 justify-end">
              <button onClick={() => { setEditing(null); setCreating(false); setForm(emptyForm); }}
                className="px-5 py-2.5 border rounded-lg text-sm font-medium hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} disabled={saveMutation.isPending || !form.titleEn}
                className="px-5 py-2.5 bg-yellow-700 text-white rounded-lg text-sm font-semibold hover:bg-yellow-800 disabled:opacity-50">
                {saveMutation.isPending ? 'Saving...' : (editing ? 'Save Changes' : 'Create Exam')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
